import { Input, Label, Slider, Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import type { ItemInput } from '@creanizer/types'

interface ItemFormModesProps {
  input: ItemInput
  onChange: (input: ItemInput) => void
}

export function ItemStandardMode({ input, onChange }: ItemFormModesProps) {
  return (
    <>
      <div>
        <Label htmlFor="name">アイテム名（オプション）</Label>
        <Input
          id="name"
          placeholder="例: エクスカリバー、不死鳥の羽根..."
          value={input.name || ''}
          onChange={(e) => onChange({ ...input, name: e.target.value })}
        />
      </div>

      <div>
        <Label htmlFor="itemType">アイテムタイプ</Label>
        <Select
          value={input.itemType || 'equipment'}
          onValueChange={(value) => onChange({ ...input, itemType: value as ItemInput['itemType'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="アイテムタイプを選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="consumable">消耗品</SelectItem>
            <SelectItem value="equipment">装備品</SelectItem>
            <SelectItem value="weapon">武器</SelectItem>
            <SelectItem value="armor">防具</SelectItem>
            <SelectItem value="accessory">アクセサリー</SelectItem>
            <SelectItem value="material">素材</SelectItem>
            <SelectItem value="key_item">キーアイテム</SelectItem>
            <SelectItem value="tool">道具</SelectItem>
            <SelectItem value="magic_item">魔法アイテム</SelectItem>
            <SelectItem value="currency">通貨</SelectItem>
            <SelectItem value="document">文書</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {input.itemType === 'custom' && (
        <div>
          <Label htmlFor="customType">カスタムタイプ</Label>
          <Input
            id="customType"
            placeholder="独自のアイテムタイプを入力"
            value={input.customType || ''}
            onChange={(e) => onChange({ ...input, customType: e.target.value })}
          />
        </div>
      )}

      <div>
        <Label htmlFor="category">カテゴリ（オプション）</Label>
        <Input
          id="category"
          placeholder="例: 回復薬、両手剣、指輪..."
          value={input.category || ''}
          onChange={(e) => onChange({ ...input, category: e.target.value })}
        />
      </div>
    </>
  )
}

export function ItemIdeaMode({ input, onChange }: ItemFormModesProps) {
  return (
    <>
      <div>
        <Label htmlFor="ideaText">アイデアテキスト</Label>
        <Textarea
          id="ideaText"
          placeholder="例: 時を止める懐中時計、呪われた剣、万能薬..."
          value={input.ideaText || ''}
          onChange={(e) => onChange({ ...input, ideaText: e.target.value })}
          rows={4}
        />
        <p className="text-sm text-muted-foreground mt-2">
          アイデアやコンセプトを自由に入力してください。参考資料や画像は詳細設定からアップロードできます。
        </p>
      </div>
    </>
  )
}

export function ItemBatchMode({ input, onChange }: ItemFormModesProps) {
  return (
    <>
      <div>
        <Label htmlFor="batchTheme">バッチテーマ</Label>
        <Input
          id="batchTheme"
          placeholder="例: 冒険者の基本装備セット、魔法学院の教材..."
          value={input.batchTheme || ''}
          onChange={(e) => onChange({ ...input, batchTheme: e.target.value })}
        />
      </div>

      <div>
        <Label htmlFor="numberOfItems">生成数: {input.numberOfItems || 5}個</Label>
        <Slider
          id="numberOfItems"
          min={1}
          max={25}
          step={1}
          value={[input.numberOfItems || 5]}
          onValueChange={([value]) => onChange({ ...input, numberOfItems: value })}
        />
        <p className="text-xs text-muted-foreground mt-2">
          ※ 5個ごとに1回のAPIを使用します（例: 18個 = 4回のAPI使用）
        </p>
      </div>

      <div>
        <Label htmlFor="batchPattern">生成パターン</Label>
        <Select
          value={input.batchPattern || 'random'}
          onValueChange={(value) => onChange({ ...input, batchPattern: value as ItemInput['batchPattern'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="生成パターンを選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="set">セット（関連アイテム）</SelectItem>
            <SelectItem value="random">ランダム（多様性）</SelectItem>
            <SelectItem value="progression">段階的（成長）</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="rarityDistribution">レア度分布</Label>
        <Select
          value={input.rarityDistribution || 'auto'}
          onValueChange={(value) => onChange({ ...input, rarityDistribution: value as ItemInput['rarityDistribution'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="レア度分布を選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">自動（バランス良く）</SelectItem>
            <SelectItem value="same">統一</SelectItem>
            <SelectItem value="weighted">重み付け（コモン多め）</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {input.rarityDistribution === 'same' && (
        <div>
          <Label htmlFor="sameRarity">統一レア度</Label>
          <Select
            value={input.sameRarity || 'common'}
            onValueChange={(value) => onChange({ ...input, sameRarity: value as ItemInput['rarity'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="統一レア度を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="common">コモン</SelectItem>
              <SelectItem value="uncommon">アンコモン</SelectItem>
              <SelectItem value="rare">レア</SelectItem>
              <SelectItem value="epic">エピック</SelectItem>
              <SelectItem value="legendary">レジェンダリー</SelectItem>
              <SelectItem value="mythic">ミシック</SelectItem>
              <SelectItem value="unique">ユニーク</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  )
}
